import { ref, nextTick } from 'vue';
import { computeExitNodeIds, stripInternalNodeData } from '../utils/flowExitNodes';
import { serializeFlowEdges, serializeFlowState } from '../utils/flowSerialization';

export function useFlowPersistence({ workspaceId, editorId, config, nodes, edges, flow, localizeError }) {
  const isSaving = ref(false);
  const hasUnsavedChanges = ref(false);
  const showUnsavedChangesModal = ref(false);
  const pendingNavigation = ref(null);
  const originalFlowState = ref(null);
  // Grafo a cui si riferiscono gli errori di validazione correnti (quello
  // caricato, quello inviato al server o quello bloccato lato client): il banner
  // li declassa a "non aggiornati" solo quando il grafo se ne discosta.
  const validatedFlowState = ref(null);
  const validationStale = ref(false);

  const currentFlowState = () => serializeFlowState({ name: flow.value.name, nodes: nodes.value, edges: edges.value });

  // Gli errori del backend arrivano col testo di fallback: chi ci usa puo'
  // tradurli (per codice) prima che finiscano nel banner e sui nodi.
  const localize = (errors) => (localizeError ? errors.map((error) => localizeError(error)) : errors);

  const saveOriginalFlowState = () => {
    originalFlowState.value = currentFlowState();
    validatedFlowState.value = originalFlowState.value;
    hasUnsavedChanges.value = false;
    validationStale.value = false;
  };

  const checkForChanges = () => {
    if (!originalFlowState.value) { hasUnsavedChanges.value = false; return; }
    const current = currentFlowState();
    hasUnsavedChanges.value = current !== originalFlowState.value;
    // "Non aggiornata: salva per rivalidare" ha senso solo se si puo' salvare.
    validationStale.value = hasUnsavedChanges.value && current !== validatedFlowState.value;
  };

  const fetchFlow = async (router) => {
    try {
      const response = await config.fetchFn(workspaceId.value, editorId.value);
      flow.value = config.getFlowFromResponse(response);

      if (flow.value.definition?.nodes?.length > 0) {
        const loadedNodes = flow.value.definition.nodes;
        const loadedEdges = flow.value.definition.edges || [];

        const exitNodeIds = config.exitNodeIndicators ? computeExitNodeIds(loadedNodes, loadedEdges) : null;
        const storedErrors = localize(flow.value.validationErrors || []);
        const errorByNodeId = {};
        for (const e of storedErrors) {
          if (e.nodeId) {
            errorByNodeId[e.nodeId] = errorByNodeId[e.nodeId]
              ? { ...errorByNodeId[e.nodeId], message: `${errorByNodeId[e.nodeId].message} | ${e.message}` }
              : e;
          }
        }
        nodes.value = loadedNodes.map(n => ({
          ...n,
          selected: false,
          data: {
            ...n.data,
            ...(exitNodeIds ? { __isExitNode: exitNodeIds.has(n.id) } : {}),
            __validationError: errorByNodeId[n.id] || null,
          },
        }));
        edges.value = loadedEdges;
        if (!config.exitNodeIndicators) {
          await nextTick();
          saveOriginalFlowState();
        }
        // Compute maxId from the same `dndnode_<N>` pattern the editor uses to
        // generate new IDs — otherwise nodeIdCounter starts at 2 and collides
        // with existing dndnode_2/3/... nodes, silently overwriting them via
        // Vue Flow's addNodes upsert behavior.
        const maxId = Math.max(0, ...nodes.value.map(n => parseInt(n.id.split('_')[1] || 0)));
        return { maxId, validationErrors: storedErrors };
      } else {
        nodes.value = config.mode === 'flow'
          ? [{ id: '1', type: 'input', data: { intentPhrases: '' }, position: { x: 250, y: 5 }, selected: false }]
          : [];
        edges.value = [];
        await nextTick();
        saveOriginalFlowState();
        return { maxId: 1 };
      }
    } catch (error) {
      Console.error('[useFlowPersistence] fetch error:', error);
      router.push(`/app/workspace/${workspaceId.value}/bots`);
      return null;
    }
  };

  /**
   * Salva il flusso.
   * @returns {Promise<'saved'|'invalid'|'failed'>} 'invalid' se la validazione
   *   lato client lo ha bloccato (errori gia' passati a onValidationErrors),
   *   'failed' se il salvataggio non e' andato a buon fine: chi chiama
   *   decide come avvisare.
   */
  const saveFlow = async (onValidationErrors) => {
    if (config.validation === 'client' && config.validateFn) {
      const errs = config.validateFn(nodes.value, edges.value);
      if (errs.length > 0) {
        // Descrivono il grafo attuale: restano attuali finche' non lo si modifica.
        validatedFlowState.value = currentFlowState();
        validationStale.value = false;
        if (onValidationErrors) onValidationErrors(errs);
        return 'invalid';
      }
    }

    isSaving.value = true;
    try {
      const payload = {
        name: flow.value.name,
        definition: {
          nodes: stripInternalNodeData(nodes.value),
          edges: serializeFlowEdges(edges.value),
        },
      };
      // Il riferimento per le modifiche non salvate e' cio' che il server riceve,
      // non lo stato al ritorno della risposta: quanto modificato durante il
      // salvataggio resta da salvare.
      const sentFlowState = serializeFlowState({ name: payload.name, ...payload.definition });

      const response = await config.saveFn(workspaceId.value, editorId.value, payload);

      const savedFlow = config.getFlowFromResponse(response);
      if (savedFlow.validationErrors !== undefined) {
        const validationErrors = localize(savedFlow.validationErrors || []);
        if (onValidationErrors) onValidationErrors(validationErrors);
        const errorByNodeId = {};
        for (const e of validationErrors) {
          if (e.nodeId) {
            errorByNodeId[e.nodeId] = errorByNodeId[e.nodeId]
              ? { ...errorByNodeId[e.nodeId], message: `${errorByNodeId[e.nodeId].message} | ${e.message}` }
              : e;
          }
        }
        // Gli errori descrivono il grafo inviato: se e' cambiato durante il
        // salvataggio sono gia' stantii, quindi i marker restano spenti e il
        // banner li segnala come non aggiornati.
        const editedWhileSaving = currentFlowState() !== sentFlowState;
        const exitNodeIds = config.exitNodeIndicators ? computeExitNodeIds(nodes.value, edges.value) : null;
        nodes.value = nodes.value.map(node => ({
          ...node,
          data: {
            ...node.data,
            __validationError: editedWhileSaving ? null : (errorByNodeId[node.id] || null),
            ...(exitNodeIds ? { __isExitNode: exitNodeIds.has(node.id) } : {}),
          },
        }));
      }

      originalFlowState.value = sentFlowState;
      validatedFlowState.value = sentFlowState;
      checkForChanges();
      return 'saved';
    } catch (error) {
      Console.error('[useFlowPersistence] save error:', error);
      return 'failed';
    } finally {
      isSaving.value = false;
    }
  };

  return {
    isSaving,
    hasUnsavedChanges,
    validationStale,
    showUnsavedChangesModal,
    pendingNavigation,
    checkForChanges,
    saveOriginalFlowState,
    computeExitNodeIds,
    fetchFlow,
    saveFlow,
  };
}
